import {
  Box,
  Dialog,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { CloseRounded } from "@mui/icons-material";
import { overlaySurface } from "../overviewPanel";

const RAIL = "26px";

// Ben's copy (earnings-hub-plan.md §7.5), with the second point rewritten
// for the hub's report cards (a count of the notes and the latest one). It
// names no plan or price, so native shows the same sheet.
export const FEED_SHEET_TITLE = "What's in this feed";
export const FEED_SHEET_LEAD =
  "Every company on your watchlist, every time it reports.";
export const FEED_SHEET_SECTIONS = [
  {
    title: "Every report, as it lands",
    blurb:
      "Each company gets a card when it reports: EPS and revenue against the estimate, and the share price move since the report. UK and Canadian companies are included; most UK companies report twice a year.",
  },
  {
    title: "What analysts did next",
    blurb:
      "After a report, its card counts the upgrades, downgrades and price-target changes from the firms covering the company, and shows the latest. Open the card for every note. Analyst notes come from US-listed coverage, so a small company, or one listed only in London or Toronto, often has none. Its results still show.",
  },
  {
    title: "Since report",
    blurb:
      "The return runs from the last close before the company reported: the previous day's close for a before-open report, the same day's close for an after-close report. It updates live, and it's left off when we can't tell which close that was.",
  },
] as const;

interface EarningsFeedCapabilitiesSheetProps {
  open: boolean;
  onClose: () => void;
}

/** "What's in this feed", in the screener capabilities layout without the preset chips. */
export const EarningsFeedCapabilitiesSheet = ({
  open,
  onClose,
}: EarningsFeedCapabilitiesSheetProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const hairline = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)";
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth={false}
      aria-labelledby="earnings-feed-sheet-title"
      slotProps={{ backdrop: { sx: { backgroundColor: "rgba(0,0,0,0.35)" } } }}
      PaperProps={{
        elevation: 0,
        sx: {
          width: fullScreen ? "100%" : "min(720px, 94vw)",
          maxHeight: fullScreen ? "100%" : "88vh",
          m: fullScreen ? 0 : 2,
          borderRadius: fullScreen ? 0 : 3,
          backgroundColor: overlaySurface(isDark),
          backgroundImage: "none",
          border: fullScreen ? "none" : `1px solid ${hairline}`,
          boxShadow: "none",
          position: "relative",
        },
      }}
    >
      <IconButton
        aria-label="Close"
        onClick={onClose}
        size="small"
        sx={{
          position: "absolute",
          top: fullScreen ? "calc(12px + env(safe-area-inset-top, 0px))" : 12,
          right: 12,
          zIndex: 1,
          backgroundColor: isDark
            ? "rgba(33, 33, 33, 0.88)"
            : "rgba(255, 255, 255, 0.88)",
          color: theme.palette.text.secondary,
          "&:hover": {
            backgroundColor: isDark
              ? "rgba(255, 255, 255, 0.1)"
              : "rgba(0, 0, 0, 0.06)",
          },
        }}
      >
        <CloseRounded sx={{ fontSize: 19 }} />
      </IconButton>
      <Box
        data-testid="earnings-feed-sheet"
        sx={{
          overflowY: "auto",
          px: { xs: 2.5, sm: 4 },
          pt: fullScreen ? "calc(24px + env(safe-area-inset-top, 0px))" : 4,
          pb: "calc(24px + env(safe-area-inset-bottom, 0px))",
        }}
      >
        <Typography
          id="earnings-feed-sheet-title"
          component="h2"
          sx={{
            fontSize: "1.15rem",
            fontWeight: 600,
            letterSpacing: "-0.01em",
            pr: 5,
            mb: 0.75,
          }}
        >
          {FEED_SHEET_TITLE}
        </Typography>
        <Typography
          sx={{
            fontSize: "0.875rem",
            color: theme.palette.text.secondary,
            lineHeight: 1.5,
            mb: 3,
          }}
        >
          {FEED_SHEET_LEAD}
        </Typography>
        {FEED_SHEET_SECTIONS.map((section, index) => (
          <Box
            key={section.title}
            sx={{
              py: 2.5,
              borderBottom:
                index === FEED_SHEET_SECTIONS.length - 1
                  ? "none"
                  : `1px solid ${hairline}`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5 }}>
              <Typography
                sx={{
                  width: RAIL,
                  flex: "none",
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  color: "#22c55e",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {String(index + 1).padStart(2, "0")}
              </Typography>
              <Typography
                component="h3"
                sx={{ fontSize: "0.9375rem", fontWeight: 600 }}
              >
                {section.title}
              </Typography>
            </Box>
            <Typography
              sx={{
                pl: `calc(${RAIL} + 12px)`,
                mt: 0.5,
                fontSize: "0.8125rem",
                color: theme.palette.text.secondary,
                lineHeight: 1.55,
              }}
            >
              {section.blurb}
            </Typography>
          </Box>
        ))}
      </Box>
    </Dialog>
  );
};
